/**
 * Text or number input with a clear (×) button shown while it has a value.
 *
 * Clearing sets the value through the native setter and dispatches a bubbling `input` event, which
 * is what React listens for. The field's own onChange therefore runs with '' and no per-field
 * handler has to change. Read-only and disabled inputs never show the button.
 */
import React, { useRef } from 'react';
import { CloseIcon } from '@atlas-forms/ui-components-react';
import '../../styles/tabs/ClearableInput.css';

type ClearableInputProps = React.InputHTMLAttributes<HTMLInputElement>;

const nativeValueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;

export const ClearableInput = React.forwardRef<HTMLInputElement, ClearableInputProps>(function ClearableInput(
  { value, disabled, readOnly, ...rest },
  forwardedRef,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setRef = (el: HTMLInputElement | null) => {
    inputRef.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  const hasValue = value !== undefined && value !== null && String(value) !== '';
  const showClear = hasValue && !disabled && !readOnly;

  const handleClear = () => {
    const input = inputRef.current;
    if (!input) return;
    nativeValueSetter?.call(input, '');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  };

  return (
    <div className="af-properties-tab__clearable">
      <input ref={setRef} value={value} disabled={disabled} readOnly={readOnly} {...rest} />
      {showClear && (
        <button
          type="button"
          className="af-properties-tab__clear"
          onClick={handleClear}
          aria-label="Clear"
          title="Clear"
        >
          <CloseIcon width={12} height={12} />
        </button>
      )}
    </div>
  );
});
